import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CurrencyEur,
  Receipt,
  Plus,
  Trash,
  ChartLine,
  CheckCircle,
  Clock,
  Warning,
  XCircle,
  File,
  PencilSimple,
  Eye,
  Paperclip,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
// A5 / P4#2 — le bandeau « Situation financière » (SituationsKPI) est retiré : un seul
// bloc de chiffres, celui des situations de travaux (SituationsTravaux).
import FactureOCRButton from './e/FactureOCRButton';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
// RP74 / D31 — réglage « le client voit les montants »
import ReglageMontantsClient from './ReglageMontantsClient';
import { projectRights } from './projectRights';
import { toast } from 'sonner';
import { DocumentPreview } from './ProjectDocuments';
import SituationsTravaux from './SituationsTravaux';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statutConfig = {
  en_attente: { label: 'En attente', color: 'bg-[#FFF8E1] text-[#B8860B] border-[#B8860B]', icon: Clock },
  validee: { label: 'Validée', color: 'bg-[#E3F2FD] text-[#1E3A5F] border-[#1E3A5F]', icon: CheckCircle },
  payee: { label: 'Payée', color: 'bg-[#E8F5E9] text-[#2D6A4F] border-[#2D6A4F]', icon: CheckCircle },
  rejetee: { label: 'Rejetée', color: 'bg-[#FFEBEE] text-[#8B3A3A] border-[#8B3A3A]', icon: XCircle },
};

const EMPTY_FACTURE = {
  numero: '', entreprise: '', lot: '', montant_ht: '', tva: '20',
  date_emission: '', date_echeance: '', description: '', statut: 'en_attente', notes: '',
};

const calcTTC = (ht, tva) => {
  const h = parseFloat(ht) || 0;
  const t = parseFloat(tva) || 0;
  return h * (1 + t / 100);
};

const formatEur = (n) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2 });

// FIN-01 — Formulaire déclaré au niveau module (et non dans le render du parent) :
// un composant recréé à chaque rendu change de type à chaque frappe, React le
// remonte et le champ perd le focus après chaque caractère.
function FactureForm({ form, setForm, onSubmit, onCancel, isEdit }) {
  const set = (key) => (e) => { const v = e.target.value; setForm(f => ({ ...f, [key]: v })); };
  return (
    <form onSubmit={onSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>N° de facture</Label><Input value={form.numero} onChange={set('numero')} placeholder="FAC-2026-006" required className="rounded-none mt-1" /></div>
        <div><Label>Entreprise</Label><Input value={form.entreprise} onChange={set('entreprise')} placeholder="Bâti Pro SARL" required className="rounded-none mt-1" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Lot</Label><Input value={form.lot} onChange={set('lot')} placeholder="Gros Oeuvre" className="rounded-none mt-1" /></div>
        <div>
          <Label>Statut</Label>
          <Select value={form.statut} onValueChange={v => setForm(f => ({ ...f, statut: v }))}>
            <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="en_attente">En attente</SelectItem>
              <SelectItem value="validee">Validée</SelectItem>
              <SelectItem value="payee">Payée</SelectItem>
              <SelectItem value="rejetee">Rejetée</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div><Label>Montant HT (€)</Label><Input type="number" step="0.01" value={form.montant_ht} onChange={set('montant_ht')} placeholder="5400.00" required className="rounded-none mt-1" /></div>
        <div><Label>TVA (%)</Label><Input type="number" step="0.1" value={form.tva} onChange={set('tva')} className="rounded-none mt-1" /></div>
        <div>
          <Label>TTC calculé</Label>
          <div className="mt-1 h-9 flex items-center px-3 bg-[#F5F5F3] border border-[#E0E0DB] text-sm font-medium text-[#2C2C2C]">
            {formatEur(calcTTC(form.montant_ht, form.tva))} €
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Date d'émission</Label><Input type="date" value={form.date_emission} onChange={set('date_emission')} className="rounded-none mt-1" /></div>
        <div><Label>Date d'échéance</Label><Input type="date" value={form.date_echeance} onChange={set('date_echeance')} className="rounded-none mt-1" /></div>
      </div>
      <div><Label>Description</Label><Textarea value={form.description} onChange={set('description')} placeholder="Description de la facture..." rows={2} className="rounded-none mt-1" /></div>
      <div><Label>Notes internes</Label><Textarea value={form.notes} onChange={set('notes')} placeholder="Notes..." rows={2} className="rounded-none mt-1" /></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-none">Annuler</Button>
        <Button type="submit" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">{isEdit ? 'Enregistrer' : 'Ajouter'}</Button>
      </div>
    </form>
  );
}

export default function ProjectFinances({ chantierId, chantier }) {
  const { user } = useAuth();
  // RP02 — avant : `amo` avait « Ajouter une facture », refusé par le serveur.
  // Écriture des factures = rôles projet MOE (POST/PUT /factures, server.py).
  const { perms, role_projet, loading: permsLoading } = usePermissions(chantierId);
  const { canWriteFactures: canEdit } = projectRights({ userRole: user?.role, roleProjet: role_projet, perms });
  const isAmo = role_projet === 'amo';
  // RP02 — GET /factures refusé (AMO/consultant) : on le dit au lieu d'afficher 0 €.
  const [facturesForbidden, setFacturesForbidden] = useState(false);
  const [factures, setFactures] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingFacture, setEditingFacture] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FACTURE });
  const [previewDoc, setPreviewDoc] = useState(null);

  // A5 / P4#11 — l'AMO ne lit pas les factures simples (réservées au cabinet) : aucun
  // appel interdit, donc plus de message rouge « Permission insuffisante » à l'ouverture.
  useEffect(() => {
    if (permsLoading || isAmo) return;
    fetchFactures();
    fetchDocuments();
  }, [chantierId, permsLoading, isAmo]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFactures = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/factures`, { withCredentials: true });
      setFactures(data);
      setFacturesForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) setFacturesForbidden(true);
    } finally { setLoading(false); }
  };

  const fetchDocuments = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/documents/${chantierId}`, { withCredentials: true });
      setDocuments(data);
    } catch { /* */ }
  };

  const openAdd = () => {
    setEditingFacture(null);
    setForm({ ...EMPTY_FACTURE });
    setAddDialogOpen(true);
  };

  const openEdit = (f) => {
    setEditingFacture(f);
    setForm({
      numero: f.numero || '',
      entreprise: f.entreprise || '',
      lot: f.lot || '',
      montant_ht: f.montant_ht ?? '',
      tva: f.tva ?? '20',
      date_emission: f.date_emission?.slice(0, 10) || '',
      date_echeance: f.date_echeance?.slice(0, 10) || '',
      description: f.description || '',
      statut: f.statut || 'en_attente',
      notes: f.notes || '',
    });
    setEditDialogOpen(true);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/factures`, {
        ...form, montant_ht: parseFloat(form.montant_ht), tva: parseFloat(form.tva),
      }, { withCredentials: true });
      toast.success('Facture ajoutée');
      setAddDialogOpen(false);
      fetchFactures();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!editingFacture) return;
    try {
      const payload = {
        ...form,
        montant_ht: form.montant_ht ? parseFloat(form.montant_ht) : undefined,
        tva: form.tva ? parseFloat(form.tva) : undefined,
      };
      await axios.put(`${API_URL}/api/chantiers/${chantierId}/factures/${editingFacture.id}`, payload, { withCredentials: true });
      toast.success('Facture mise à jour');
      setEditDialogOpen(false);
      fetchFactures();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteFacture = async (factureId) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/factures/${factureId}`, { withCredentials: true });
      toast.success('Facture supprimée');
      fetchFactures();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleUpdateStatut = async (factureId, newStatut) => {
    try {
      await axios.put(`${API_URL}/api/chantiers/${chantierId}/factures/${factureId}`, { statut: newStatut }, { withCredentials: true });
      toast.success('Statut mis à jour');
      fetchFactures();
    } catch { toast.error('Erreur'); }
  };

  const findLinkedDoc = (facture) => {
    if (facture.document_id) return documents.find(d => d.id === facture.document_id);
    const num = (facture.numero || '').toLowerCase();
    const ent = (facture.entreprise || '').toLowerCase();
    return documents.find(d => {
      const n = (d.name || '').toLowerCase();
      return d.stored_name && (d.type === 'facture') && (
        (num && n.includes(num)) || (ent && n.includes(ent))
      );
    });
  };

  const totalHT = factures.reduce((s, f) => s + (f.montant_ht || 0), 0);
  const totalTTC = factures.reduce((s, f) => s + calcTTC(f.montant_ht, f.tva), 0);
  const totalPayee = factures.filter(f => f.statut === 'payee').reduce((s, f) => s + calcTTC(f.montant_ht, f.tva), 0);
  const totalEnAttente = factures.filter(f => f.statut === 'en_attente').reduce((s, f) => s + calcTTC(f.montant_ht, f.tva), 0);
  const fmt = formatEur;

  return (
    <div data-testid="project-finances">
      {/* RP74 / D31 — l'architecte décide si le client voit les montants (Paiements) */}
      {!isAmo && <ReglageMontantsClient chantierId={chantierId} />}
      {/* A5 / P4#16 — les situations de travaux d'abord ; les factures simples (hors
          marchés : honoraires, frais…) en sous-partie. RP41 / D7 — l'AMO ne voit que les situations. */}
      <Tabs key={isAmo ? 'amo' : 'std'} defaultValue="situations" className="w-full">
        {!isAmo && (
        <TabsList className="rounded-none bg-[#F5F5F3] border border-[#E0E0DB] h-auto p-0 mb-5 flex-wrap">
          {/* S1b/FIN-10 — situations v2 réservées aux rôles projet MOE (can_see_finances) */}
          <TabsTrigger value="situations" className="rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#C4773B] px-5 py-2" data-testid="tab-situations">
            Situations de travaux
          </TabsTrigger>
          <TabsTrigger value="factures" className="rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#C4773B] px-5 py-2 text-[#6B6B6B]" data-testid="tab-factures">
            Factures simples (hors situations)
          </TabsTrigger>
        </TabsList>
        )}

        <TabsContent value="situations" className="mt-0">
          <SituationsTravaux chantierId={chantierId} roleProjet={role_projet} />
        </TabsContent>

        <TabsContent value="factures" className="mt-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>Factures simples</h2>
          <p className="text-xs text-[#6B6B6B] mt-1">Factures hors situations de travaux (honoraires, frais, fournitures). Elles n'entrent pas dans les chiffres des marchés.</p>
        </div>
        {canEdit && (
          <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none gap-2" onClick={openAdd} data-testid="add-facture-button">
            <Plus size={18} weight="bold" />Ajouter une facture
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card-kroquis text-center">
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-1">Budget</p>
          <p className="text-2xl font-bold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>{(chantier?.budget || 0).toLocaleString('fr-FR')} €</p>
        </div>
        {!facturesForbidden && (<>
        <div className="card-kroquis text-center">
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-1">Total facturé TTC</p>
          <p className="text-2xl font-bold text-[#C75B39]" style={{ fontFamily: 'DM Serif Display, serif' }}>{fmt(totalTTC)} €</p>
        </div>
        <div className="card-kroquis text-center">
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-1">Payé</p>
          <p className="text-2xl font-bold text-[#2D6A4F]" style={{ fontFamily: 'DM Serif Display, serif' }}>{fmt(totalPayee)} €</p>
        </div>
        <div className="card-kroquis text-center">
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-1">En attente</p>
          <p className="text-2xl font-bold text-[#B8860B]" style={{ fontFamily: 'DM Serif Display, serif' }}>{fmt(totalEnAttente)} €</p>
        </div>
        </>)}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : facturesForbidden ? (
        <div className="card-kroquis text-center py-16" data-testid="factures-not-shared">
          <Receipt size={64} className="mx-auto text-[#E0E0DB] mb-4" />
          <p className="text-[#6B6B6B]">Les factures de ce projet ne sont pas partagées avec vous.</p>
        </div>
      ) : factures.length === 0 ? (
        <div className="card-kroquis text-center py-16">
          <Receipt size={64} className="mx-auto text-[#E0E0DB] mb-4" />
          <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">Aucune facture</h3>
          {canEdit && <p className="text-[#6B6B6B] mb-4">Commencez par ajouter des factures à ce projet</p>}
        </div>
      ) : (
        <div className="card-kroquis overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full table-kroquis" data-testid="factures-table">
              <thead>
                <tr>
                  <th className="text-left">N°</th>
                  <th className="text-left">Entreprise</th>
                  <th className="text-left">Lot</th>
                  <th className="text-right">HT</th>
                  <th className="text-center">TVA</th>
                  <th className="text-right">TTC</th>
                  <th className="text-left">Émission</th>
                  <th className="text-left">Statut</th>
                  <th className="text-center">Doc</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {factures.map(facture => {
                  const config = statutConfig[facture.statut] || statutConfig.en_attente;
                  const linkedDoc = findLinkedDoc(facture);
                  const ttc = calcTTC(facture.montant_ht, facture.tva);
                  return (
                    <tr key={facture.id} className="hover:bg-[#FAFAF8] transition-colors cursor-pointer" onClick={() => canEdit && openEdit(facture)} data-testid={`facture-row-${facture.id}`}>
                      <td className="font-medium text-[#2C2C2C]">{facture.numero}</td>
                      <td>{facture.entreprise}</td>
                      <td className="text-[#6B6B6B]">{facture.lot || '—'}</td>
                      <td className="text-right font-medium">{fmt(facture.montant_ht)} €</td>
                      <td className="text-center text-[#6B6B6B] text-xs">{facture.tva}%</td>
                      <td className="text-right font-medium">{fmt(ttc)} €</td>
                      <td className="text-[#6B6B6B]">{facture.date_emission ? new Date(facture.date_emission).toLocaleDateString('fr-FR') : '—'}</td>
                      <td onClick={e => e.stopPropagation()}>
                        {canEdit ? (
                          <Select value={facture.statut} onValueChange={v => handleUpdateStatut(facture.id, v)}>
                            <SelectTrigger className={`rounded-none text-xs h-7 w-28 border ${config.color}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="en_attente">En attente</SelectItem>
                              <SelectItem value="validee">Validée</SelectItem>
                              <SelectItem value="payee">Payée</SelectItem>
                              <SelectItem value="rejetee">Rejetée</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`inline-block text-xs px-2 py-1 border ${config.color}`}>{config.label || facture.statut}</span>
                        )}
                      </td>
                      <td className="text-center" onClick={e => e.stopPropagation()}>
                        {linkedDoc ? (
                          <Button variant="ghost" size="sm" className="rounded-none h-7 w-7 p-0 text-[#C4773B]" onClick={() => setPreviewDoc(linkedDoc)} title="Voir la facture" data-testid={`view-facture-doc-${facture.id}`}>
                            <Eye size={14} />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="rounded-none h-7 w-7 p-0 text-[#D0D0D0] cursor-default" title="Aucun document rattaché — uploadez la facture dans l'onglet Documents" disabled>
                            <Paperclip size={14} />
                          </Button>
                        )}
                      </td>
                      <td className="text-center" onClick={e => e.stopPropagation()}>
                        {canEdit ? (
                          <div className="flex items-center justify-center gap-1">
                            {/* Phase E E.5 — OCR Claude Vision sur la facture */}
                            <FactureOCRButton
                              facture={facture}
                              chantierId={chantierId}
                              onUpdate={() => fetchFactures()}
                            />
                            <Button variant="ghost" size="sm" className="rounded-none h-7 w-7 p-0" onClick={() => openEdit(facture)} data-testid={`edit-facture-${facture.id}`}><PencilSimple size={14} /></Button>
                            <Button variant="ghost" size="sm" className="rounded-none text-[#8B3A3A] hover:bg-red-50 h-7 w-7 p-0" onClick={() => handleDeleteFacture(facture.id)} data-testid={`delete-facture-${facture.id}`}><Trash size={14} /></Button>
                          </div>
                        ) : (
                          <span className="text-xs text-[#9A9A9A]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary */}
      {factures.length > 0 && (
        <div className="mt-6 p-4 bg-[#F5F5F3] border border-[#E0E0DB]">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#6B6B6B]">{factures.length} facture{factures.length > 1 ? 's' : ''}</span>
            <div className="flex items-center gap-6">
              <span>HT: <strong>{fmt(totalHT)} €</strong></span>
              <span>TTC: <strong>{fmt(totalTTC)} €</strong></span>
            </div>
          </div>
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Ajouter une facture</DialogTitle></DialogHeader>
          <FactureForm form={form} setForm={setForm} onSubmit={handleSubmitAdd} onCancel={() => setAddDialogOpen(false)} isEdit={false} />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Modifier — {editingFacture?.numero}</DialogTitle></DialogHeader>
          <FactureForm form={form} setForm={setForm} onSubmit={handleSubmitEdit} onCancel={() => setEditDialogOpen(false)} isEdit={true} />
        </DialogContent>
      </Dialog>

      {/* Document Preview */}
      {previewDoc && <DocumentPreview document={previewDoc} chantierId={chantierId} onClose={() => setPreviewDoc(null)} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
